import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Center, Group, Stack, Text, Title } from "@mantine/core";
import { ArrowLeft, Compass, MapPinOff } from "lucide-react";
import Sentry from "@/lib/sentry";

/**
 * Shown for any path the portal has no route for.
 *
 * Rendered INSIDE the AppShell, unlike the management app's standalone
 * NotFound: a member who mistypes a URL or follows a stale bookmark should
 * keep the bottom nav, because getting back to something useful is the only
 * thing they want from this screen.
 */
export default function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Recorded at warning level, not as an exception. A member landing on a
    // dead link is not a crash, and 9 redirects were retired recently — the
    // point of this signal is to see which retired paths are still being
    // followed (so one can be restored if it turns out to be load-bearing),
    // not to page anyone. captureException would put every typo in the error
    // feed alongside real breakage.
    Sentry.captureMessage(`Portal 404: ${location.pathname}`, {
      level: "warning",
      tags: { errorType: "404", path: location.pathname },
    });
  }, [location.pathname]);

  return (
    <Center mih="60vh" px="md">
      <Card withBorder padding="xl" maw={420} w="100%">
        <Stack align="center" gap="sm">
          <MapPinOff size={40} style={{ color: "var(--mantine-color-dimmed)" }} />
          <Title order={2} fz="xl" fw={600} ta="center">
            This page doesn't exist
          </Title>
          <Text c="dimmed" fz="sm" ta="center">
            The link may be out of date, or the page may have moved. Your
            dashboard has everything that's still here.
          </Text>
          <Group mt="md" gap="sm">
            {/* -1 rather than a fixed path: the common arrival is a mistyped
                or stale link followed from somewhere the member wants back. */}
            <Button variant="default" onClick={() => navigate(-1)} leftSection={<ArrowLeft size={16} />}>
              Go back
            </Button>
            <Button onClick={() => navigate("/")} leftSection={<Compass size={16} />}>
              Dashboard
            </Button>
          </Group>
        </Stack>
      </Card>
    </Center>
  );
}
